import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { formatUtcTime } from "@/lib/clock";
import { applyFilters, carryFilters } from "@/lib/filters";

import { getTab, messageFor, STALE_TIME, type ApiParams, type Enveloped } from "./api";
import { apiParams, CLEAR_ALL, readShared } from "./filters";
import { usePublishPageMeta } from "./pageMeta";
import type { SourcingTab } from "./routes";

export const COPY = {
  noItem: "Pick an item to see its ranking.",
  noItemOption: "Pick an item, then an option, to see the option's overview.",
  noOption: "Pick an option to see its overview.",
  noVariant: "Pick a variant to see its offers.",
  noMatch: "No options match these filters.",
  clear: "Clear filters",
  loadError: "Couldn't load this page.",
  retry: "Try again",
  openViewHealth: "Open View Health",
} as const;

export function failedCopy(checkedAt: string | null): string {
  const time = formatUtcTime(checkedAt);
  const when = time ? ` at ${time}` : "";
  return `This page failed its data check${when}. Numbers hidden so no one reads wrong data. Ioan has been told.`;
}

export type FrameContext = { item: string | null; option: string | null; variant: string | null };

const str = (value: unknown): string | null =>
  typeof value === "string" && value !== "" ? value : null;

export function frameContext(search: Record<string, unknown>): FrameContext {
  const shared = readShared(search);
  return {
    item: shared.item,
    option: str(search.option) ?? shared.option,
    variant: str(search.f_variant),
  };
}

export function needsMet(tab: SourcingTab, context: FrameContext): boolean {
  if (tab.needs === "item") return context.item !== null;
  if (tab.needs === "option") return context.option !== null;
  if (tab.needs === "variant") return context.variant !== null;
  return true;
}

export function useTabQuery<T>(tab: SourcingTab, extra: ApiParams = {}) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const context = frameContext(search);
  const pageExtra = tab.needs === "option" && context.option
    ? { ...extra, option: context.option }
    : extra;
  const params = apiParams(tab, readShared(search), pageExtra);
  return useQuery({
    queryKey: ["sourcing_funnel", tab.apiTab, params],
    queryFn: () => getTab<T>(tab.apiTab, params),
    enabled: needsMet(tab, context),
    staleTime: STALE_TIME,
  });
}

export interface PageFrameProps<T> {
  tab: SourcingTab;
  areaName: string;
  shared: FrameContext;
  status: "pending" | "error" | "success";
  error?: unknown;
  result?: Enveloped<T>;
  onRetry: () => void;
  onClearFilters: () => void;
  onOpenViewHealth: () => void;
  isEmpty?: (data: T) => boolean;
  emptyCopy?: string;
  notFound?: NotFound<T>;
  children: (data: T) => ReactNode;
}

/**
 * For a page about one thing (an item, an option): what to say when C1 holds nothing for it.
 * Shown in place of the no-match state, whose Clear filters button could not change the answer.
 */
export interface NotFound<T> {
  when: (data: T | null) => boolean;
  message: string;
}

export function PageFrame<T>(props: PageFrameProps<T>) {
  const { tab, shared, status, result } = props;
  if (tab.needs === "item" && shared.item === null) {
    return <p className="py-8 text-center">{COPY.noItem}</p>;
  }
  if (tab.needs === "option" && shared.option === null) {
    return <p className="py-8 text-center">{shared.item === null ? COPY.noItemOption : COPY.noOption}</p>;
  }
  if (tab.needs === "variant" && shared.variant === null) {
    return <p className="py-8 text-center">{COPY.noVariant}</p>;
  }
  if (status === "pending") return <Skeleton className="h-64 w-full" aria-label="Loading" />;
  if (status === "error" || !result) {
    return (
      <div role="alert" className="flex flex-col gap-2 p-4">
        <p>{COPY.loadError}</p>
        <p>{messageFor(props.error, props.areaName)}</p>
        <Button data-nonmutating variant="outline" onClick={props.onRetry}>{COPY.retry}</Button>
      </div>
    );
  }
  if (result.meta.health.status === "failed") {
    return (
      <div role="alert" className="flex flex-col gap-2 p-4">
        <p>{failedCopy(result.meta.health.checked_at)}</p>
        <Button data-nonmutating variant="link" onClick={props.onOpenViewHealth}>{COPY.openViewHealth}</Button>
      </div>
    );
  }
  const data = result.data;
  if (props.notFound?.when(data)) return <p className="py-8 text-center">{props.notFound.message}</p>;
  if (data === null || (props.isEmpty && props.isEmpty(data))) {
    return (
      <div className="flex flex-col items-center gap-2 py-8">
        <p>{props.emptyCopy ?? COPY.noMatch}</p>
        <Button data-nonmutating variant="outline" onClick={props.onClearFilters}>{COPY.clear}</Button>
      </div>
    );
  }
  return <>{props.children(data)}</>;
}

/** Clear filters keeps the item (and the variant a page needs) and returns to page 1. */
export function clearSearch(previous: Record<string, unknown>): Record<string, unknown> {
  const next = applyFilters(previous, CLEAR_ALL);
  delete next.page;
  return next;
}

/** The two frame actions every page shares: Clear filters and Open View Health. */
export function useFrameActions() {
  const params = useParams({ strict: false }) as { area?: string; module?: string };
  const navigate = useNavigate();
  return {
    clearFilters: () =>
      void navigate({
        search: ((previous: Record<string, unknown>) => clearSearch(previous)) as never,
      } as never),
    openViewHealth: () =>
      void navigate({
        to: "/app/$area/$module/$tab",
        params: {
          area: params.area ?? "sourcing",
          module: params.module ?? "sourcing_funnel",
          tab: "view_health",
        },
        search: ((previous: Record<string, unknown>) => carryFilters(previous)) as never,
      } as never),
  };
}

/**
 * The data a page head may show: null until the page has loaded, passed its
 * check and has something in it, so a head never states a figure the frame hides.
 */
export function headData<T>(
  status: "pending" | "error" | "success",
  result: Enveloped<T> | undefined,
  isEmpty?: (data: T) => boolean,
): T | null {
  if (status !== "success" || !result || result.meta.health.status === "failed") return null;
  const data = result.data;
  if (data === null || (isEmpty && isEmpty(data))) return null;
  return data;
}

export function TabPage<T>({
  tab,
  areaName,
  extra,
  isEmpty,
  emptyCopy,
  notFound,
  head,
  children,
}: {
  tab: SourcingTab;
  areaName: string;
  extra?: ApiParams;
  isEmpty?: (data: T) => boolean;
  /** Optional page-specific empty finding; keeps loading/error/health handling in the shared frame. */
  emptyCopy?: string;
  notFound?: NotFound<T>;
  /** Rendered above the frame in every state; receives the data only when the page shows it. */
  head?: (data: T | null) => ReactNode;
  children: (data: T) => ReactNode;
}) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const query = useTabQuery<T>(tab, extra);
  const actions = useFrameActions();
  const shared = frameContext(search);
  const held = needsMet(tab, shared) ? headData(query.status, query.data, isEmpty) : null;
  const shown = held !== null && notFound?.when(held) ? null : held;
  usePublishPageMeta(query.status, query.data?.meta);
  return (
    <>
      {head ? head(shown) : null}
      <PageFrame<T>
        tab={tab}
        areaName={areaName}
        shared={shared}
        status={query.status}
        error={query.error}
        result={query.data}
        isEmpty={isEmpty}
        emptyCopy={emptyCopy}
        notFound={notFound}
        onRetry={() => void query.refetch()}
        onClearFilters={actions.clearFilters}
        onOpenViewHealth={actions.openViewHealth}
      >
        {children}
      </PageFrame>
    </>
  );
}
